/** The planner's inputs as a form; every change goes up as a partial patch (spec §6.3). */
import { useState } from 'react';
import { Button, FileButton, Group, NumberInput, SegmentedControl, Select, Slider, Stack, Switch, Tabs, Text, Textarea, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useFormat } from '@/components/useFormat';
import { FINISH_ACTIONS, RC_LOST_ACTIONS, type DroneSpec, type MissionInputs } from '@/planner/types';
import { brand } from '@/theme/theme';
import { parsePolygon } from './importPolygon';
import type { EditorMode } from './polygonEditor';

export interface ParameterPanelProps {
  inputs: MissionInputs;
  onChange: (patch: Partial<MissionInputs>) => void;
  drones: DroneSpec[];
  drone: string;
  onDroneChange: (key: string) => void;
  mode: EditorMode;
  onModeChange: (mode: EditorMode) => void;
  name: string;
  notes: string;
  onNameChange: (v: string) => void;
  onNotesChange: (v: string) => void;
  /** The DTM covers the area (false also while it is still loading). */
  dtmAvailable: boolean;
  /** Interval the capped speed really yields, when the planner had to cap it. */
  effectiveInterval: number | null;
  /** The flight speed the plan derived from the interval (interval mode), shown under the field. */
  derivedSpeed: number | null;
  /** The shortest time between photos the plan yields (waypoint mode), shown under the speed field. */
  derivedInterval: number | null;
  /** The tab the panel opens on: the area for a new mission, the flight for a saved one. */
  initialTab?: PanelTab;
}

export type PanelTab = 'area' | 'flight' | 'terrain' | 'mission';

const numeric = (apply: (n: number) => void) => (v: string | number) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (Number.isFinite(n)) apply(n);
};

export function ParameterPanel(p: ParameterPanelProps) {
  const { t } = useTranslation('missions');
  const { number } = useFormat();
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const drone = p.drones.find((d) => d.key === p.drone);
  const { inputs } = p;

  const runImport = (text: string) => {
    const r = parsePolygon(text);
    if (r.ok) {
      setImportError(null);
      setImportText('');
      p.onChange({ polygon: r.polygon });
      p.onModeChange('edit');
    } else {
      setImportError(t(`panel.importError.${r.reason}`));
    }
  };

  const terrainBlocked = !inputs.takeoff ? t('panel.terrainNoTakeoff') : !p.dtmAvailable ? t('panel.terrainNoDtm') : null;

  return (
    <Tabs defaultValue={p.initialTab ?? 'area'} keepMounted={false}>
      <Tabs.List grow mb="md">
        <Tabs.Tab value="area">{t('panel.area')}</Tabs.Tab>
        <Tabs.Tab value="flight">{t('panel.flight')}</Tabs.Tab>
        <Tabs.Tab value="terrain">{t('panel.terrain')}</Tabs.Tab>
        <Tabs.Tab value="mission">{t('panel.mission')}</Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="area">
        <Stack gap="xs">
        <Group gap="xs">
          <Button size="xs" variant={p.mode === 'draw' ? 'filled' : 'default'} onClick={() => p.onModeChange(p.mode === 'draw' ? 'edit' : 'draw')}>
            {p.mode === 'draw' ? t('panel.finish') : t('panel.draw')}
          </Button>
          <Button size="xs" variant={p.mode === 'edit' ? 'filled' : 'default'} disabled={inputs.polygon.length < 3} onClick={() => p.onModeChange(p.mode === 'edit' ? 'view' : 'edit')}>
            {t('panel.edit')}
          </Button>
          <Button size="xs" variant="subtle" color="red" disabled={inputs.polygon.length === 0} onClick={() => p.onChange({ polygon: [] })}>
            {t('panel.clear')}
          </Button>
        </Group>
        <NumberInput label={t('panel.margin')} suffix={' m'} min={0} max={500} value={inputs.margin_m} onChange={numeric((n) => p.onChange({ margin_m: n }))} />
        <Textarea
          label={t('panel.import')}
          placeholder={t('panel.importPlaceholder')}
          autosize
          minRows={2}
          maxRows={4}
          value={importText}
          onChange={(e) => setImportText(e.currentTarget.value)}
          error={importError}
        />
        <Group gap="xs">
          <Button size="xs" variant="default" disabled={!importText.trim()} onClick={() => runImport(importText)}>
            {t('panel.importButton')}
          </Button>
          <FileButton accept=".geojson,.json,.kml,application/geo+json,application/json,application/vnd.google-earth.kml+xml" onChange={(f) => f && void f.text().then(runImport)}>
            {(props) => (
              <Button size="xs" variant="default" {...props}>
                {t('panel.importFile')}
              </Button>
            )}
          </FileButton>
        </Group>
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="flight">
        <Stack gap="xs">
        <Select data={p.drones.map((d) => ({ value: d.key, label: d.name }))} value={p.drone} allowDeselect={false} onChange={(v) => v && p.onDroneChange(v)} />
        {drone && (
          <Text fz={12} c={brand.textMuted}>
            {t('panel.cameraLine', { w: drone.camera.image_width_px, h: drone.camera.image_height_px, f: number(drone.camera.focal_mm, 2) })}
          </Text>
        )}
        <NumberInput label={t('panel.altitude')} suffix={' m'} min={10} max={500} value={inputs.altitude_m} onChange={numeric((n) => p.onChange({ altitude_m: n }))} />
        <NumberInput label={t('panel.forward')} suffix={' %'} min={0} max={95} value={inputs.forward_overlap} onChange={numeric((n) => p.onChange({ forward_overlap: n }))} />
        <NumberInput label={t('panel.side')} suffix={' %'} min={0} max={95} value={inputs.side_overlap} onChange={numeric((n) => p.onChange({ side_overlap: n }))} />
        <Switch label={t('panel.angleAuto')} checked={inputs.angle_deg === null} onChange={(e) => p.onChange({ angle_deg: e.currentTarget.checked ? null : 90 })} />
        {inputs.angle_deg !== null && (
          <Stack gap={2}>
            <Text fz={12} c={brand.textMuted}>
              {t('panel.angle')}: {number(inputs.angle_deg)}°
            </Text>
            <Slider min={0} max={179} step={1} value={inputs.angle_deg} onChange={(v) => p.onChange({ angle_deg: v })} label={(v) => `${v}°`} />
          </Stack>
        )}
        <Stack gap={2}>
          <Text fz={12} c={brand.textMuted}>
            {t('panel.trigger')}
          </Text>
          <SegmentedControl
            value={inputs.trigger}
            onChange={(v) => p.onChange({ trigger: v as MissionInputs['trigger'] })}
            data={[
              { value: 'interval', label: t('panel.triggerInterval') },
              { value: 'waypoint', label: t('panel.triggerWaypoint') },
            ]}
          />
        </Stack>
        {inputs.trigger === 'interval' ? (
          <>
          <NumberInput
            label={t('panel.interval')}
            suffix={' s'}
            min={0.5}
            max={60}
            step={0.5}
            value={inputs.interval_s}
            onChange={numeric((n) => p.onChange({ interval_s: n }))}
            description={p.effectiveInterval !== null ? t('warnings.speed_capped', { interval: number(p.effectiveInterval, 1) }) : undefined}
          />
          {p.derivedSpeed !== null && (
            <Text fz={12} c={brand.textMuted}>
              {t('panel.derivedSpeed', { speed: number(p.derivedSpeed, 1) })}
            </Text>
          )}
          </>
        ) : (
          <>
          <NumberInput label={t('panel.speed')} suffix={' m/s'} min={0.5} max={drone?.max_speed_ms ?? 15} step={0.1} decimalScale={1} value={inputs.speed_ms} onChange={numeric((n) => p.onChange({ speed_ms: n }))} />
          {p.derivedInterval !== null && (
            <Text fz={12} c={brand.textMuted}>
              {t('panel.derivedInterval', { seconds: number(p.derivedInterval, 1) })}
            </Text>
          )}
          </>
        )}
        <NumberInput label={t('panel.hover')} suffix={' s'} min={0} max={30} step={0.5} value={inputs.hover_s} onChange={numeric((n) => p.onChange({ hover_s: n }))} />
        <NumberInput label={t('panel.gimbalPitch')} suffix="°" min={-90} max={0} step={5} value={inputs.gimbal_pitch_deg} onChange={numeric((n) => p.onChange({ gimbal_pitch_deg: n }))} />
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="terrain">
        <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Text fz={14}>{t('panel.takeoff')}</Text>
          <Group gap="xs">
            <Button size="xs" variant={p.mode === 'takeoff' ? 'filled' : 'default'} onClick={() => p.onModeChange(p.mode === 'takeoff' ? 'view' : 'takeoff')}>
              {t('panel.takeoffPlace')}
            </Button>
            {inputs.takeoff && (
              <Button size="xs" variant="subtle" onClick={() => p.onChange({ takeoff: null, terrain_follow: false })}>
                {t('panel.takeoffClear')}
              </Button>
            )}
          </Group>
        </Group>
        <Text fz={12} c={brand.textMuted}>
          {inputs.takeoff ? `${number(inputs.takeoff[1], 5)}, ${number(inputs.takeoff[0], 5)}` : t('panel.takeoffNone')}
        </Text>
        <Switch
          label={t('panel.terrainFollow')}
          checked={inputs.terrain_follow}
          disabled={terrainBlocked !== null}
          description={terrainBlocked ?? undefined}
          onChange={(e) => p.onChange({ terrain_follow: e.currentTarget.checked })}
        />
        {inputs.terrain_follow && (
          <NumberInput label={t('panel.terrainTolerance')} suffix={' m'} min={0.5} max={50} step={0.5} value={inputs.terrain_tolerance_m} onChange={numeric((n) => p.onChange({ terrain_tolerance_m: n }))} />
        )}
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="mission">
        <Stack gap="xs">
        <TextInput label={t('panel.name')} required withAsterisk={false} value={p.name} onChange={(e) => p.onNameChange(e.currentTarget.value)} />
        <Textarea label={t('panel.notes')} autosize minRows={2} value={p.notes} onChange={(e) => p.onNotesChange(e.currentTarget.value)} />
        <Select
          label={t('panel.rcLost')}
          data={RC_LOST_ACTIONS.map((v) => ({ value: v, label: t(`panel.rcLostOptions.${v}`) }))}
          value={inputs.rc_lost}
          allowDeselect={false}
          onChange={(v) => v && p.onChange({ rc_lost: v as MissionInputs['rc_lost'] })}
        />
        <Select
          label={t('panel.finish')}
          data={FINISH_ACTIONS.map((v) => ({ value: v, label: t(`panel.finishOptions.${v}`) }))}
          value={inputs.finish}
          allowDeselect={false}
          onChange={(v) => v && p.onChange({ finish: v as MissionInputs['finish'] })}
        />
        </Stack>
      </Tabs.Panel>
    </Tabs>
  );
}
